import React, { useState } from 'react';
import { Bookmark, MapPin, DollarSign, Clock, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
export interface JobProps {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  tags: string[];
  isPromoted?: boolean;
  logo?: string;
}
interface JobCardProps {
  job: JobProps;
  variant?: 'horizontal' | 'vertical';
  index?: number;
}
export function JobCard({
  job,
  variant = 'vertical',
  index = 0
}: JobCardProps) {
  const [isBookmarked, setIsBookmarked] = useState(false);
  const isHorizontal = variant === 'horizontal';
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay: index * 0.1,
    ease: [0.25, 0.46, 0.45, 0.94]
  }} whileHover={{
    y: -4
  }} className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/30 p-4 relative flex flex-col ${isHorizontal ? 'min-w-[280px] w-[280px]' : 'w-full'} transition-colors duration-200`} data-id="element-22">
      {/* Header */}
      <div className="flex justify-between items-start mb-3" data-id="element-23">
        <div className="flex items-center gap-3" data-id="element-24">
          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden" data-id="element-25">
            {job.logo ? <img src={job.logo} alt={job.company} className="w-full h-full object-cover" data-id="element-26" /> : <Briefcase className="w-6 h-6 text-gray-400 dark:text-gray-500" data-id="element-27" />}
          </div>
          <div data-id="element-28">
            <h3 className="font-bold text-gray-900 dark:text-white leading-tight" data-id="element-29">
              {job.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-30">
              {job.company}, {job.location}
            </p>
          </div>
        </div>
        <motion.button className={`transition-colors ${isBookmarked ? 'text-[#00D084]' : 'text-gray-400 dark:text-gray-500'}`} onClick={() => setIsBookmarked(!isBookmarked)} whileTap={{
        scale: 0.8
      }} animate={{
        scale: isBookmarked ? [1, 1.3, 1] : 1
      }} transition={{
        duration: 0.3
      }} data-id="element-31">
          <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-current' : ''}`} data-id="element-32" />
        </motion.button>
      </div>

      {/* Promoted Badge */}
      {job.isPromoted && <motion.div initial={{
      opacity: 0,
      x: -10
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.2
    }} className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400 mb-3" data-id="element-33">
          <motion.span className="w-2 h-2 rounded-full bg-green-500" animate={{
        scale: [1, 1.2, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} data-id="element-34" />
          Active recruiting
        </motion.div>}

      {/* Details */}
      <div className="space-y-2 mb-4 flex-1" data-id="element-35">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-36">
          <DollarSign className="w-4 h-4 text-gray-400 dark:text-gray-500" data-id="element-37" />
          <span data-id="element-38">{job.salary}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-39">
          <Briefcase className="w-4 h-4 text-gray-400 dark:text-gray-500" data-id="element-40" />
          <span data-id="element-41">{job.type}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400" data-id="element-42">
          <Clock className="w-4 h-4 text-gray-400 dark:text-gray-500" data-id="element-43" />
          <span data-id="element-44">Full time - Internship</span>
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-4" data-id="element-45">
        {job.tags.map((tag, tagIndex) => <motion.span key={tagIndex} initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3 + tagIndex * 0.05
      }} className="px-2 py-1 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-md" data-id="element-46">
            {tag}
          </motion.span>)}
      </div>

      {/* Action */}
      <motion.button whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className="w-full bg-[#FF5722] hover:bg-[#F4511E] text-white font-semibold py-2.5 rounded-xl transition-colors shadow-md" data-id="element-47">
        Apply now
      </motion.button>
    </motion.div>;
}